import React from 'react';
import SinatraLogo from './SinatraLogo';
import AccountMenu from './AccountMenu';
import Icon from './Icon';
import './TopBar.css';

/** The app's permanent header: brand, the back button when drilled into a company, the Filters
 * toggle (opens FiltersDialog, rendered by Graph.js), zoom controls (graph view only), the theme
 * toggle, the account menu, and the pagination row underneath. A real full-width bar beside the
 * Sidebar, not the old floating island. */
const TopBar = ({
  backAvailable,
  backLabel,
  backCount,
  onBack,
  viewMode,
  groupBy = 'industry',
  theme,
  setTheme,
  contacts,
  loading,
  paginationData,
  loadAllMode,
  onPageChange,
  onPageSizeChange,
  activeApiFilters = [],
  selectedIndustry,
  onResetZoom,
  showFilters,
  onToggleFilters,
  zoomLevel,
  pinnedZoom,
  onPinZoom
}) => (
  <header className={`app-header${loading ? ' loading' : ''}`}>
    <div className="app-header__row">
      <div className="app-header__titlebar">
        <span className="app-header__logo"><SinatraLogo /></span>

        {backAvailable && viewMode !== 'tree' && (
          <button
            type="button"
            className="top-bar-back-button"
            onClick={onBack}
            title={`Back to ${groupBy === 'icp' ? 'ICP lists' : 'Industries'} (${backLabel})`}
          >
            <Icon name="arrow-left" />
            <span className="top-bar-back-button__label">Back to {groupBy === 'icp' ? 'ICP lists' : 'Industries'}</span>
            <span className="top-bar-back-button__meta">({backLabel} · {backCount} {backCount === 1 ? 'person' : 'people'})</span>
          </button>
        )}
      </div>

      <div className="app-header__actions">
        {viewMode !== 'pipeline' && viewMode !== 'dashboard' && (
          <button
            type="button"
            onClick={() => {
              onToggleFilters && onToggleFilters();
            }}
            className={`top-bar-perspective-button ${showFilters ? 'active' : ''}`}
            title={`${showFilters ? 'Hide' : 'Show'} filters`}
            aria-expanded={showFilters}
          >
            <span className="button-icon"><Icon name="filter" /></span>
            <span className="button-text">Filters</span>
            {activeApiFilters.length > 0 && <span className="filter-count">{activeApiFilters.length}</span>}
          </button>
        )}

        <div className={`zoom-controls ${viewMode !== 'graph' ? 'hidden' : ''}`}>
          <button
            onClick={onResetZoom}
            className="top-bar-reset-zoom-button"
            title="Fit graph to screen"
            aria-label="Fit graph to screen"
            style={{ visibility: viewMode === 'graph' && onResetZoom ? 'visible' : 'hidden' }}
          >
            <Icon name="fit-view" />
          </button>

          {zoomLevel && (
            <div className="zoom-percentage">
              {Math.round(zoomLevel * 100)}%
              {pinnedZoom && (
                <span className="pin-indicator" title="Zoom is pinned"><Icon name="pin" /></span>
              )}
            </div>
          )}

          {zoomLevel && onPinZoom && (
            <button
              onClick={onPinZoom}
              className={`zoom-pin-button ${pinnedZoom && Math.abs(pinnedZoom - zoomLevel) < 0.01 ? 'pinned' : ''}`}
              title={pinnedZoom && Math.abs(pinnedZoom - zoomLevel) < 0.01 ? "Unpin zoom level" : "Pin current zoom level"}
              aria-label={pinnedZoom && Math.abs(pinnedZoom - zoomLevel) < 0.01 ? "Unpin zoom level" : "Pin current zoom level"}
              aria-pressed={Boolean(pinnedZoom && Math.abs(pinnedZoom - zoomLevel) < 0.01)}
            >
              <Icon name="pin" />
            </button>
          )}
        </div>

        <button
          onClick={setTheme}
          className="top-bar-theme-button"
          title={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
        >
          <Icon name={theme === 'light' ? 'moon' : 'sun'} />
        </button>

        <AccountMenu />
      </div>
    </div>

    {!loading && !loadAllMode && viewMode !== 'tree' && viewMode !== 'pipeline' && viewMode !== 'dashboard' && paginationData?.total_pages > 1 && (
      <div className="top-bar-pagination">
        <span className="pagination-label">Show</span>
        <select
          value={paginationData.page_size || 50}
          onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
          className="page-size-select"
          aria-label="Items per page"
        >
          <option value={10}>10</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span className="pagination-label">per page</span>
        <div className="page-controls">
          <button
            type="button"
            aria-label="Previous page"
            onClick={() => onPageChange(paginationData.page - 1)}
            disabled={paginationData.page <= 1}
            className="page-btn"
          >
            <Icon name="chevron-left" />
          </button>
          <span className="page-info">
            {paginationData.page} of {paginationData.total_pages}
          </span>
          <button
            type="button"
            aria-label="Next page"
            onClick={() => onPageChange(paginationData.page + 1)}
            disabled={paginationData.page >= paginationData.total_pages}
            className="page-btn"
          >
            <Icon name="chevron-right" />
          </button>
        </div>
        {paginationData?.total_items > 0 && (
          <span className="pagination-total-count">
            ({paginationData.total_items} total items
            {selectedIndustry && (
              <span className="industry-stats">
                • {selectedIndustry.company_count} co • {selectedIndustry.contact_count} contacts
              </span>
            )})
          </span>
        )}
      </div>
    )}

    {!loading && loadAllMode && viewMode !== 'tree' && viewMode !== 'pipeline' && viewMode !== 'dashboard' && (
      <div className="top-bar-stats-bottom">
        {`${contacts?.length || 0} contacts (all loaded)`}
      </div>
    )}
  </header>
);

export default TopBar;
